import { Client } from '@stomp/stompjs';
import { z } from 'zod';
import {
    appendMessage,
    ErrorCodes,
    fetchSnapshot,
    getBuildupData,
    HTTPStatusCodes,
    setTickers,
} from './api';
import orderBookInstance from './OrderBook';
import userPortfolio from './UserPortfolio';
import CandlestickTracker from './CandlestickTracker';
import { SeqBuffer } from './SeqBuffer';
import { getWsBaseUrl } from '../config/runtime';
import { createLogger } from '../utils/logger';
import { safeJsonParse, safeParse } from '../utils/validation';
import {
    AdminWsMessageSchema,
    ChartUpdateSchema,
    OrderBookSnapshotSchema,
    OrderBookUpdatesSchema,
    PortfolioMessageSchema,
} from '../domain/schemas';

const log = createLogger('SocketManager');

const TOPICS = Object.freeze({
    ORDERBOOK: '/topic/orderbook',
    PRIVATE: '/user/queue/private',
    ADMIN: '/user/queue/admin',
    CHART: '/topic/chart',
});

const DEFAULT_PERIODIC_RESNAPSHOT_MS = 10_000;
const PERIODIC_RESNAPSHOT_MS = (() => {
    const raw = process.env.REACT_APP_ORDERBOOK_RESNAPSHOT_MS;
    if (raw === undefined) return DEFAULT_PERIODIC_RESNAPSHOT_MS;
    const n = Number(raw);
    return Number.isFinite(n) ? n : DEFAULT_PERIODIC_RESNAPSHOT_MS;
})();

type OrderBookUpdates = z.infer<typeof OrderBookUpdatesSchema>;

type AppliedOrderBookUpdate = {
    seq: number;
    source: 'ws' | 'rest';
    updates: OrderBookUpdates;
    appliedAt: number;
};

function buildBrokerUrl({ sessionToken, username }) {
    const base = getWsBaseUrl();
    const url = new URL(base);
    url.pathname = '/exchange-socket';
    url.searchParams.set('Session-ID', sessionToken);
    url.searchParams.set('Username', username);
    return url.toString();
}

class SocketManager {
    public stompClient: Client | null;
    public connected: boolean;

    private lastAppliedSeq: number | null;
    private seqBuffer: SeqBuffer;
    private pendingBySeq: Map<number, OrderBookUpdates>;
    private gapFillInProgress: boolean;
    private needsResnapshot: boolean;
    private resnapshotInProgress: boolean;
    private periodicResnapshotTimer: number | null;
    private pendingSnapshotReset: any | null;

    private appliedUpdates: AppliedOrderBookUpdate[];
    private maxAppliedUpdates: number;

    constructor() {
        this.stompClient = null;
        this.connected = false;

        // Update recovery (seq) state
        this.lastAppliedSeq = null;
        this.seqBuffer = new SeqBuffer(1000);
        this.pendingBySeq = new Map();
        this.gapFillInProgress = false;
        this.needsResnapshot = false;
        this.resnapshotInProgress = false;
        this.periodicResnapshotTimer = null;
        this.pendingSnapshotReset = null;

        // Buffer of applied updates (for debugging / replay / UI introspection)
        this.appliedUpdates = [];
        this.maxAppliedUpdates = 1000;
    }

    isReady() {
        return Boolean(this.stompClient && this.connected);
    }

    // Initialize and configure the WebSocket connection
    async connect() {
        const buildupData = getBuildupData();

        if (!buildupData || !buildupData.sessionToken || !buildupData.username) {
            log.error('Buildup data is incomplete or unavailable; cannot connect');
            return;
        }

        if (this.stompClient) {
            // Avoid duplicated clients if connect() is called multiple times.
            this.disconnect();
        }

        // Initialize seq from a fresh snapshot. If this fails, we can still run, but gap-fill may be limited.
        if (this.lastAppliedSeq === null) {
            await this.resnapshotAndResetSeq({ reason: 'initial-connect' });
        }

        const brokerURL = buildBrokerUrl(buildupData);

        // Create a new STOMP client
        this.stompClient = new Client({
            brokerURL,
            debug: (str) => {
                //console.log(str); // Debugging logs
            },
            reconnectDelay: 5000, // Reconnect after 5 seconds if disconnected
            heartbeatIncoming: 4000, // Client heartbeat
            heartbeatOutgoing: 4000, // Server heartbeat
        });

        // Define event handlers
        this.stompClient.onConnect = async (frame) => {
            this.connected = true;

            if (this.needsResnapshot) {
                await this.resnapshotAndResetSeq({ reason: 'reconnect' });
                this.needsResnapshot = false;
            }

            this.subscribeToTopics();
            this.startPeriodicResnapshot();
        };

        this.stompClient.onDisconnect = () => {
            this.connected = false;
            this.stopPeriodicResnapshot();
        };

        this.stompClient.onWebSocketError = (error) => {
            log.error('WebSocket error', error);
        };

        this.stompClient.onWebSocketClose = () => {
            // On reconnect, re-onboard from snapshot; local state may be stale.
            this.needsResnapshot = true;
            this.stopPeriodicResnapshot();
        };

        this.stompClient.onStompError = (frame) => {
            log.error('STOMP error', {
                message: frame?.headers?.message,
                body: frame?.body,
            });
        };

        // Activate the WebSocket connection
        this.stompClient.activate();
    }

    // Subscribe to specific topics
    subscribeToTopics() {
        if (!this.stompClient || !this.connected) {
            log.error('Cannot subscribe: WebSocket client is not connected');
            return;
        }

        // Subscribe to public orderbook updates
        this.stompClient.subscribe(TOPICS.ORDERBOOK, (message) => {
            const parsed = safeJsonParse(message.body);
            if (!parsed) {
                log.warn('Invalid JSON on orderbook topic');
                return;
            }
            this.handleOrderbookMessage(parsed);
        });

        // Subscribe to private user-specific updates
        this.stompClient.subscribe(TOPICS.PRIVATE, (message) => {
            const parsed = safeJsonParse(message.body);
            if (!parsed) {
                log.warn('Invalid JSON on private topic');
                return;
            }
            this.handlePrivateMessage(parsed);
        });

        // Subscribe to admin-to-user messages (simple text payload)
        this.stompClient.subscribe(TOPICS.ADMIN, (message) => {
            const parsed = safeJsonParse(message.body);
            if (!parsed) {
                log.warn('Invalid JSON on admin topic');
                return;
            }
            this.handleAdminMessage(parsed);
        });

        this.stompClient.subscribe(TOPICS.CHART, (message) => {
            const parsed = safeJsonParse(message.body);
            if (!parsed) {
                log.warn('Invalid JSON on chart topic');
                return;
            }
            this.handleChartUpdate(parsed);
        });
    }

    // Disconnect from the WebSocket server
    disconnect() {
        if (this.stompClient) {
            this.stompClient.deactivate();
            this.connected = false;
            this.stompClient = null;
        }
        this.stopPeriodicResnapshot();
    }

    startPeriodicResnapshot() {
        // Allow disabling via env var.
        if (!Number.isFinite(PERIODIC_RESNAPSHOT_MS) || PERIODIC_RESNAPSHOT_MS <= 0) return;
        if (this.periodicResnapshotTimer !== null) return;

        this.periodicResnapshotTimer = window.setInterval(() => {
            // Best-effort: keep the order book baseline fresh in case we missed websocket deltas.
            // Skip if disconnected or already resnapshotting.
            if (!this.connected) return;
            if (this.gapFillInProgress) return;
            if (this.resnapshotInProgress) return;
            void this.resnapshotAndResetSeq({ reason: 'periodic' });
        }, PERIODIC_RESNAPSHOT_MS);
    }

    stopPeriodicResnapshot() {
        if (this.periodicResnapshotTimer === null) return;
        window.clearInterval(this.periodicResnapshotTimer);
        this.periodicResnapshotTimer = null;
    }

    // Handle incoming public orderbook messages
    async handleOrderbookMessage(data) {
        // snapshot_reset is broadcast by the backend on set_tickers to replace all tickers/books.
        if (data && data.type === 'snapshot_reset') {
            await this.handleSnapshotReset(data);
            return;
        }

        const incomingSeq = this.extractSeq(data);
        const updates = this.extractUpdates(data);

        // Heartbeat / non-update payloads: do not mutate orderbook.
        if (!updates) {
            return;
        }

        const validatedUpdates = safeParse(OrderBookUpdatesSchema, updates, {
            source: 'ws',
            topic: 'orderbook',
            seq: incomingSeq,
        });
        if (!validatedUpdates) {
            return;
        }

        // If server isn't sending seq yet, fall back to best-effort.
        if (incomingSeq === null) {
            orderBookInstance.updateVolumes(validatedUpdates);
            return;
        }

        // Deduplicate
        if (this.seqBuffer.has(incomingSeq)) {
            return;
        }

        // If we don't have a baseline yet, try to snapshot now.
        if (this.lastAppliedSeq === null) {
            await this.resnapshotAndResetSeq({ reason: 'missing-baseline' });
        }

        // During a gap-fill, buffer incoming updates by seq.
        if (this.gapFillInProgress || this.resnapshotInProgress) {
            this.pendingBySeq.set(incomingSeq, validatedUpdates);
            return;
        }

        await this.applyWithGapFill(incomingSeq, validatedUpdates);
        await this.drainPendingInOrder();
    }

    extractSeq(data) {
        if (!data || typeof data !== 'object') return null;
        const raw = data.seq ?? data.sequence ?? data.sequenceNumber;
        const num = Number(raw);
        return Number.isFinite(num) ? num : null;
    }

    extractUpdates(data) {
        if (!data || typeof data !== 'object') return null;
        const content = data.content;

        // Some servers may return updates directly instead of a JSON string.
        if (Array.isArray(content)) return content;

        if (typeof content !== 'string') {
            return null;
        }

        try {
            const parsed = JSON.parse(content);
            return Array.isArray(parsed) ? parsed : null;
        } catch (_) {
            return null;
        }
    }

    markApplied(seq: number, updates: OrderBookUpdates, source: AppliedOrderBookUpdate['source']) {
        this.seqBuffer.add(seq);
        this.lastAppliedSeq = seq;

        this.appliedUpdates.push({
            seq,
            source,
            updates,
            appliedAt: Date.now(),
        });

        if (this.appliedUpdates.length > this.maxAppliedUpdates) {
            this.appliedUpdates.splice(0, this.appliedUpdates.length - this.maxAppliedUpdates);
        }
    }

    prunePendingUpToSeq(inclusiveSeq: number) {
        // Drop any buffered updates that are already covered by the current baseline.
        // (After a snapshot, everything <= lastAppliedSeq is already reflected.)
        const keysToDelete: number[] = [];
        this.pendingBySeq.forEach((_, seq) => {
            if (seq <= inclusiveSeq) {
                keysToDelete.push(seq);
            }
        });
        keysToDelete.forEach((seq) => this.pendingBySeq.delete(seq));
    }

    async applyWithGapFill(incomingSeq: number, updates: OrderBookUpdates) {
        // If we somehow receive an older seq, just dedupe/skip.
        if (this.lastAppliedSeq !== null && incomingSeq <= this.lastAppliedSeq) {
            this.seqBuffer.add(incomingSeq);
            return;
        }

        this.gapFillInProgress = true;
        try {
            // If we see a gap, take a fresh snapshot and continue from there.
            if (this.lastAppliedSeq !== null && incomingSeq > this.lastAppliedSeq + 1) {
                await this.resnapshotAndResetSeq({ reason: 'gap' });
            }

            // If we resnapshotted during gap fill, the snapshot may already include this incoming update.
            if (this.lastAppliedSeq !== null && incomingSeq <= this.lastAppliedSeq) {
                this.seqBuffer.add(incomingSeq);
                return;
            }

            // Apply the incoming websocket update.
            if (!this.seqBuffer.has(incomingSeq)) {
                orderBookInstance.updateVolumes(updates);
                this.markApplied(incomingSeq, updates, 'ws');
            }
        } finally {
            this.gapFillInProgress = false;
        }
    }

    async resnapshotAndResetSeq({ reason }) {
        if (this.resnapshotInProgress) return;

        this.resnapshotInProgress = true;
        let baselineReset = false;
        try {
            const snap = await fetchSnapshot();
            if (!snap || snap.status !== HTTPStatusCodes.OK) {
                log.warn('Snapshot request failed', { reason, snap });
                return;
            }

            // Accept either { orderBookData, latestSeq } or { snapshot, latestSeq }.
            const book = snap.orderBookData ?? snap.snapshot ?? snap.orderbook ?? null;
            const latestSeq = Number(
                snap.latestSeq ?? snap.seq ?? snap.sequence ?? snap.sequenceNumber,
            );

            if (!book || !Number.isFinite(latestSeq)) {
                log.warn('Snapshot missing fields', { snap });
                return;
            }

            // If orderBookData is a JSON string, parse it.
            const parsedBook = typeof book === 'string' ? safeJsonParse(book) : book;
            if (!parsedBook) {
                log.warn('Snapshot book is invalid JSON', { snap });
                return;
            }

            const validatedBook = safeParse(OrderBookSnapshotSchema, parsedBook, {
                source: 'rest',
                endpoint: 'snapshot',
                reason,
            });
            if (!validatedBook) {
                return;
            }

            orderBookInstance.resetFromSnapshot(validatedBook);

            // Backend returns the *next* seq to be allocated (SeqGenerator.get()).
            // The snapshot includes all updates up to (latestSeq - 1).
            this.lastAppliedSeq = latestSeq - 1;
            this.seqBuffer.clear();

            // Keep any buffered updates that are newer than this baseline;
            // drop anything already covered by the snapshot.
            this.prunePendingUpToSeq(this.lastAppliedSeq);

            this.appliedUpdates.length = 0;
            baselineReset = true;
        } catch (e) {
            log.warn('Resnapshot failed', { reason, error: e });
        } finally {
            this.resnapshotInProgress = false;
        }

        // If updates arrived while we were resnapshotting, apply any contiguous tail.
        if (baselineReset) {
            await this.drainPendingInOrder();
        }

        // If we received an admin-triggered snapshot_reset while resnapshotting,
        // apply it now so tickers stay consistent.
        if (this.pendingSnapshotReset) {
            const queued = this.pendingSnapshotReset;
            this.pendingSnapshotReset = null;
            await this.handleSnapshotReset(queued);
        }
    }

    async drainPendingInOrder() {
        if (this.lastAppliedSeq === null) return;
        while (this.pendingBySeq.has(this.lastAppliedSeq + 1)) {
            const nextSeq = this.lastAppliedSeq + 1;
            const nextUpdates = this.pendingBySeq.get(nextSeq);
            this.pendingBySeq.delete(nextSeq);
            if (nextUpdates) {
                await this.applyWithGapFill(nextSeq, nextUpdates);
            }
        }
    }

    // Handle a server-broadcast snapshot reset triggered by set_tickers.
    // Replaces the entire order book and ticker list for all connected users.
    async handleSnapshotReset(data) {
        if (this.resnapshotInProgress) {
            // Queue the latest snapshot_reset; applying it is more important than
            // any intermediate snapshots because it also carries the ticker list.
            this.pendingSnapshotReset = data;
            return;
        }

        this.resnapshotInProgress = true;
        let baselineReset = false;
        try {
            const snapshotRaw = data.snapshot;
            const latestSeq = Number(data.latestSeq);
            const tickerList: string[] = Array.isArray(data.tickers) ? data.tickers : [];

            if (snapshotRaw !== undefined && Number.isFinite(latestSeq)) {
                const parsedBook =
                    typeof snapshotRaw === 'string' ? safeJsonParse(snapshotRaw) : snapshotRaw;

                const validatedBook = parsedBook
                    ? safeParse(OrderBookSnapshotSchema, parsedBook, {
                          source: 'ws',
                          event: 'snapshot_reset',
                      })
                    : null;

                if (validatedBook) {
                    orderBookInstance.resetFromSnapshot(validatedBook);
                    // Same semantics as REST snapshot: latestSeq is the *next* seq.
                    this.lastAppliedSeq = latestSeq - 1;
                    this.seqBuffer.clear();

                    this.prunePendingUpToSeq(this.lastAppliedSeq);

                    this.appliedUpdates.length = 0;
                    baselineReset = true;
                } else {
                    log.warn('snapshot_reset: invalid snapshot payload, skipping orderbook reset');
                }
            }

            if (tickerList.length > 0) {
                setTickers(tickerList);
            }
        } finally {
            this.resnapshotInProgress = false;
        }

        if (baselineReset) {
            await this.drainPendingInOrder();
        }
    }

    // Handle incoming private messages
    handlePrivateMessage(data) {
        const validated = safeParse(PortfolioMessageSchema, data, {
            source: 'ws',
            topic: 'private',
        });
        if (!validated) return;

        userPortfolio.updatePortfolio(validated);
    }
    handleChartUpdate(data) {
        const validated = safeParse(ChartUpdateSchema, data, {
            source: 'ws',
            topic: 'chart',
        });
        if (!validated) return;

        Object.entries(validated).forEach(([ticker, ohlc]) => {
            CandlestickTracker.insertCandle(ticker, {
                open: ohlc.open,
                high: ohlc.high,
                low: ohlc.low,
                close: ohlc.close,
                timestamp: Date.now(),
            });
        });
    }

    handleAdminMessage(data) {
        const validated = safeParse(AdminWsMessageSchema, data, {
            source: 'ws',
            topic: 'admin',
        });
        if (!validated) return;

        if (validated.type !== 'admin_message') return;

        appendMessage(`Admin: ${validated.message}`, ErrorCodes.SUCCESS);
    }

    // Add your logic to process private messages

    // Publish messages to a specific destination
    sendMessage(destination, body) {
        if (!this.isReady()) {
            log.error('Cannot send message: WebSocket client is not connected');
            return;
        }

        this.stompClient.publish({
            destination,
            body: JSON.stringify(body),
        });

        //console.log(`Message sent to ${destination}:`, body);
    }
}

// Export a singleton instance of SocketManager
const socketManager = new SocketManager();
export default socketManager;
